import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";

export const HIGHLIGHT_COLORS = {
  yellow: "rgba(250, 204, 21, 0.55)", // amber
  pink:   "rgba(244, 114, 182, 0.5)", // pink-400
  green:  "rgba(74, 222, 128, 0.5)",  // green-400
  blue:   "rgba(96, 165, 250, 0.5)",  // blue-400
};
export const HIGHLIGHT_COLOR_SWATCH = {
  yellow: "#facc15",
  pink:   "#f472b6",
  green:  "#4ade80",
  blue:   "#60a5fa",
};

/**
 * Text-selection highlighter with a small color picker popup.
 * - Selection inside container shows a floating pill of 4 color swatches.
 * - Click a swatch → save via /api/highlights with color.
 * - Existing marks render with their saved color; click deletes.
 */
export default function HighlightContainer({ chapterId, sectionIdx, highlights, onChange, children }) {
  const ref = useRef(null);
  const [popup, setPopup] = useState(null); // {x, y, text}

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll("mark[data-hl]").forEach(m => {
      const parent = m.parentNode;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
      parent.normalize();
    });
    if (!highlights || highlights.length === 0) return;
    highlights.forEach(h => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => (n.parentElement && n.parentElement.closest("mark[data-hl]") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
      });
      let node;
      while ((node = walker.nextNode())) {
        const idx = node.nodeValue.indexOf(h.text);
        if (idx >= 0) {
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, idx + h.text.length);
          const mark = document.createElement("mark");
          mark.setAttribute("data-hl", h.id);
          mark.style.backgroundColor = HIGHLIGHT_COLORS[h.color] || HIGHLIGHT_COLORS.yellow;
          mark.style.padding = "0 2px";
          mark.style.borderRadius = "2px";
          mark.style.cursor = "pointer";
          mark.title = "Click to remove highlight";
          try { range.surroundContents(mark); } catch { /* skip cross-node */ }
          break;
        }
      }
    });
  }, [highlights, children]);

  const handleMouseUp = useCallback((e) => {
    const target = e.target;
    if (target && target.tagName === "MARK" && target.getAttribute("data-hl")) {
      const hid = target.getAttribute("data-hl");
      api.delete(`/highlights/${hid}`).then(() => {
        toast.success("Highlight removed");
        onChange && onChange();
      }).catch(() => toast.error("Remove failed"));
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) { setPopup(null); return; }
    const text = sel.toString().trim();
    if (!text || text.length < 3 || text.length > 500) { setPopup(null); return; }
    if (!ref.current || !ref.current.contains(sel.anchorNode)) { setPopup(null); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    setPopup({
      x: rect.left + rect.width / 2 + window.scrollX,
      y: rect.top + window.scrollY - 8,
      text,
    });
  }, [onChange]);

  const save = async (color) => {
    if (!popup) return;
    try {
      await api.post("/highlights", { chapter_id: chapterId, section_idx: sectionIdx, text: popup.text, color });
      toast.success(`Highlighted (${color})`);
      setPopup(null);
      window.getSelection()?.removeAllRanges();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Highlight failed");
    }
  };

  return (
    <div ref={ref} onMouseUp={handleMouseUp} onTouchEnd={handleMouseUp} data-testid="highlight-container">
      {children}
      {popup && (
        <div
          role="dialog"
          className="fixed z-50 -translate-x-1/2 -translate-y-full pointer-events-auto"
          style={{ left: popup.x, top: popup.y }}
          data-testid="highlight-color-picker"
        >
          <div className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2 py-1 shadow-xl">
            {Object.keys(HIGHLIGHT_COLORS).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => save(c)}
                data-testid={`hl-color-${c}`}
                aria-label={`Highlight ${c}`}
                title={`Highlight ${c}`}
                className="h-7 w-7 grid place-items-center rounded-full hover:scale-110 transition-transform"
              >
                <span
                  className="h-4 w-4 rounded-full border border-primary-foreground/60"
                  style={{ background: HIGHLIGHT_COLOR_SWATCH[c] }}
                />
              </button>
            ))}
            <button
              className="h-7 w-7 grid place-items-center rounded-full hover:bg-primary/80 ml-1"
              onClick={() => setPopup(null)}
              aria-label="Cancel"
              data-testid="hl-cancel"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
